import { BROADCAST_CHANNELS, PopupHandler, PopupWithBcHandler, randomId } from "@toruslabs/base-controllers";
import { post } from "@toruslabs/http-helpers";
import { JRPCEngine, SafeEventEmitter } from "@toruslabs/openlogin-jrpc";
import log from "loglevel";

import config from "@/config";
import { TOPUP } from "@/utils/topup/topup-enums";

const postSimplexOrder = async ({
  path,
  params,
  method = "post",
  preopenInstanceId,
  orderInstanceId,
  engine,
}: {
  path: string;
  params: any;
  engine: {
    communicationEngine?: JRPCEngine;
    communicationWindowManager?: SafeEventEmitter;
  };
  method?: string;
  preopenInstanceId: string;
  orderInstanceId: string;
}) => {
  const form = document.createElement("form");
  form.method = method;
  form.action = path;
  form.target = "form-target";
  // eslint-disable-next-line no-restricted-syntax
  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const hiddenField = document.createElement("input");
      hiddenField.type = "hidden";
      hiddenField.name = key;
      hiddenField.value = params[key];
      form.append(hiddenField);
    }
  }
  document.body.append(form);

  const channelName = `${BROADCAST_CHANNELS.REDIRECT_CHANNEL}_${orderInstanceId}`;

  const newUrl = new URL("about:blank");

  const simplexWindow = new PopupWithBcHandler({
    state: {
      url: newUrl,
      windowId: preopenInstanceId,
    },
    config: {
      dappStorageKey: config.dappStorageKey || undefined,
      communicationEngine: engine?.communicationEngine,
      communicationWindowManager: engine?.communicationWindowManager,
      target: "_blank",
    },
    instanceId: channelName,
  });

  setTimeout(() => {
    form.submit();
  }, 1000);
  await simplexWindow.handle();
  return { success: true };
};

const postOrder = async (requestObject: any, headers: any) => {
  try {
    const options = {
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...headers,
      },
    };
    return post(`${config.simplexApiHost}/order`, requestObject, options as RequestInit);
  } catch (error) {
    log.error(error);
  }
  return undefined;
};

const getOrder = async (
  params: any,
  engine: {
    communicationEngine?: JRPCEngine;
    communicationWindowManager?: SafeEventEmitter;
  }
) => {
  const preopenInstanceId = randomId();
  const finalUrl = new URL(`${config.redirect_uri}?preopenInstanceId=${preopenInstanceId}`);
  const handledWindow = new PopupHandler({
    config: {
      target: "form-target",
    },
    state: { url: finalUrl },
  });
  handledWindow.open();
  handledWindow.once("close", () => {
    throw new Error("user closed simplex popup");
  });

  const orderInstanceId = randomId();
  const instanceState = encodeURIComponent(
    window.btoa(
      JSON.stringify({
        instanceId: orderInstanceId,
        provider: TOPUP.SIMPLEX,
      })
    )
  );
  postOrder(
    {
      "g-recaptcha-response": "",
      account_details: {
        app_end_user_id: params.currentOrder.user_id,
      },
      return_url: `${config.redirect_uri}?state=${instanceState}`,
      transaction_details: {
        payment_details: {
          fiat_total_amount: {
            currency: params.currentOrder.fiat_money.currency,
            amount: params.currentOrder.fiat_money.total_amount,
          },
          requested_digital_amount: {
            currency: params.currentOrder.digital_money.currency,
            amount: params.currentOrder.digital_money.amount,
          },
          destination_wallet: {
            currency: params.currentOrder.digital_money.currency,
            address: params.address,
          },
        },
      },
    },
    {
      Authorization: `Bearer ${params.jwtToken}`,
    }
  )
    .then((result: any) => {
      const {
        version,
        partner,
        return_url,
        quote_id,
        payment_id,
        user_id,
        destination_wallet_address,
        destination_wallet_currency,
        fiat_total_amount_amount,
        fiat_total_amount_currency,
        digital_total_amount_amount,
        digital_total_amount_currency,
        payment_post_url,
      } = result.result;

      postSimplexOrder({
        preopenInstanceId,
        orderInstanceId,
        path: payment_post_url,
        params: {
          payment_flow_type: "wallet",
          version,
          partner,
          return_url,
          quote_id,
          payment_id,
          user_id,
          "destination_wallet[address]": destination_wallet_address,
          "destination_wallet[currency]": destination_wallet_currency,
          "fiat_total_amount[amount]": fiat_total_amount_amount,
          "fiat_total_amount[currency]": fiat_total_amount_currency,
          "digital_total_amount[amount]": digital_total_amount_amount,
          "digital_total_amount[currency]": digital_total_amount_currency,
        },
        engine,
      });
      return false;
    })
    .catch((error) => {
      log.error(error);
    });
  const url = `${config.simplexApiHost}`;
  return new URL(url);
};

export default {
  getOrder,
};
